import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GameLayout from '../layout/GameLayout';
import FloatingCard from '../ui/FloatingCard';
import VenueSearch from '../ui/VenueSearch';
import dropInService from '../../services/api/dropInService';
import PriceField, { dollarsToCents, priceError } from '../payments/PriceField';
import { RefundPolicyField } from '../payments/RefundPolicy';
import { eid } from '../../utils/entityId';
import { getUserSports, getAllSports } from '../../services/api/sportsService';
import { isTeamSport } from '../../config/sportsConfig';
import { dropInFormatsFor, defaultDropInFormat, formatSize, formatForSize } from '../../utils/dropInFormats';
import '../../styles/features/dropIn.css';
import { viewerTimeZone } from '../../utils/eventTime';

const SKILL_LEVELS = [
  { value: 'beginner',     label: '🌿 Beginner',     desc: 'New to the sport, learning the basics' },
  { value: 'recreational', label: '🟢 Recreational', desc: 'Casual, all-skill-levels welcome' },
  { value: 'intermediate', label: '🟡 Intermediate', desc: 'Some experience required' },
  { value: 'advanced',     label: '🔴 Advanced',     desc: 'High-skill, experienced players' },
  { value: 'professional', label: '👑 Professional', desc: 'Elite-level, competitive play' },
];

const DropInCreation = ({ token, user, onLogout }) => {
  const navigate = useNavigate();

  const [sports, setSports]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [formData, setFormData]   = useState({
    refund_policy: '',
    sport_id:      '',
    title:         '',
    description:   '',
    skill_level:   'beginner',
    max_players:   12,
    scheduled_at:  '',
    venue:         '',
    venue_address: '',
    venue_lat:     null,
    venue_lng:     null,
    venue_place_id:'',
    court:         '',
    notes:         '',
    visibility:    'public',
    max_waitlist:    5,
    price:           '',   // dollars per player; blank = free
    // Set from the sport when it is chosen (DI-B14)
    players_per_team: 6,
    play_format: '',
    is_recurring:      false,
    recurrence_day:    '',   // 0=Sun…6=Sat
    recurrence_time:   '',
    recurrence_end_time: '',
  });

  useEffect(() => {
    const loadSports = async () => {
      try {
        let sportsData = [];
        try {
          const userRes = await getUserSports();
          sportsData = userRes?.data?.sports || userRes?.sports || userRes?.data || [];
        } catch { sportsData = []; }
        if (!sportsData.length) {
          const allRes = await getAllSports();
          sportsData = allRes?.data || [];
        }
        setSports(sportsData);
      } catch { /* ignore */ }
    };
    loadSports();
  }, []);

  // The sport decides the formats on offer and the team size; picking a
  // format sets the team size and a new team size picks its format (DI-B14/B15)
  const sportNameOf = (sportId) => sports.find((s) => String(s.id) === String(sportId))?.name || '';
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'sport_id') {
        Object.assign(next, defaultDropInFormat(sportNameOf(value), prev.players_per_team));
      } else if (name === 'play_format') {
        const size = formatSize(sportNameOf(prev.sport_id), value);
        if (size) next.players_per_team = size;
      } else if (name === 'players_per_team') {
        next.play_format = formatForSize(sportNameOf(prev.sport_id), value);
      }
      return next;
    });
  };

  const handleVenueChange = (venue) => {
    setFormData((prev) => ({
      ...prev,
      venue:          venue?.venue          || '',
      venue_address:  venue?.venue_address  || '',
      venue_lat:      venue?.venue_lat      ?? null,
      venue_lng:      venue?.venue_lng      ?? null,
      venue_place_id: venue?.venue_place_id || '',
      court:          venue?.court          ?? prev.court,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sport_id)     return setError('Please select a sport.');
    if (!formData.scheduled_at) return setError('Please set a date and time.');
    if (!formData.title.trim()) return setError('Please provide a title.');
    if (!formData.venue || !formData.venue.trim()) return setError('Venue is required — players need to know where to show up.');
    if (priceError(formData.price)) return setError(priceError(formData.price));

    setLoading(true);
    setError(null);
    try {
      const { price, ...fields } = formData;
      const payload = {
        ...fields,
        price_cents: dollarsToCents(price),
        sport_id:    parseInt(formData.sport_id),
        max_players: parseInt(formData.max_players),
        // The wall time at the venue: the server reads it in the venue's zone,
        // this device's zone when the venue has no map pin
        scheduled_at: formData.scheduled_at,
        timezone:    viewerTimeZone(),
        visibility:  formData.visibility || 'public',
        max_waitlist:      parseInt(formData.max_waitlist),
        players_per_team:  parseInt(formData.players_per_team),
        play_format:       formData.play_format || null,
        is_recurring:      !!formData.is_recurring,
        recurrence_day:    formData.is_recurring && formData.recurrence_day !== '' ? parseInt(formData.recurrence_day) : null,
        recurrence_time:   formData.is_recurring ? (formData.recurrence_time || null) : null,
        recurrence_end_time: formData.is_recurring ? (formData.recurrence_end_time || null) : null,
      };
      const res = await dropInService.create(payload);
      navigate(`/drop-in/${eid(res.data.dropIn)}`)
    } catch (err) {
      setError(err.message || 'Failed to create drop-in');
    } finally {
      setLoading(false);
    }
  };

  // Compute once per render — needs the resolved sport name, not just sport_id
  const selectedSportObj = sports.find((s) => String(s.id) === String(formData.sport_id));
  const selectedSportName = selectedSportObj?.name || '';
  const isIndividualSport = selectedSportName ? !isTeamSport(selectedSportName) : false;
  const sportFormats = dropInFormatsFor(selectedSportName);
  const minDatetime = new Date(Date.now() + 30 * 60 * 1000)
    .toISOString().slice(0, 16);

  return (
    <GameLayout token={token} onLogout={onLogout}>
      <div className="dropin-form-page">
        <div className="dropin-form-header">
          <h1 className="dropin-form-title">🏃 Create Drop-in Session</h1>
          <p className="dropin-form-subtitle">
            Set up an open-play event. Anyone can join up to your max capacity.
          </p>
        </div>

        {error && (
          <div className="form-error-banner" role="alert" style={{ marginBottom: '1rem' }}>
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* 3-column grid: Event Details | Schedule & Venue | Level & Capacity */}
          <div className="dropin-form-row dropin-form-row--3col">
            {/* Col 1: Event Details */}
            <FloatingCard>
              <h3 className="form-section-title">📋 Event Details</h3>

              {/* Sport */}
              <div className="form-group-gaming">
                <label htmlFor="dropin-sport" className="form-label-gaming">Sport *</label>
                <select
                  id="dropin-sport"
                  name="sport_id"
                  value={formData.sport_id}
                  onChange={handleChange}
                  required
                  className="form-select-gaming"
                >
                  <option value="">Select a sport</option>
                  {sports.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div className="form-group-gaming">
                <label htmlFor="dropin-title" className="form-label-gaming">Title *</label>
                <input
                  id="dropin-title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Sunday Volleyball Drop-in"
                  required
                  maxLength={255}
                  className="form-input-gaming"
                />
              </div>

              {/* Description */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Any additional info for players (bring your own gear, etc.)"
                  rows={3}
                  maxLength={1000}
                  className="form-input-gaming"
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            </FloatingCard>

            {/* Col 2: Schedule & Venue */}
            <FloatingCard>
              <h3 className="form-section-title">📅 Schedule & Venue</h3>

              {/* Date/Time */}
              <div className="form-group-gaming">
                <label htmlFor="dropin-scheduled-at" className="form-label-gaming">Date & Time *</label>
                <input
                  id="dropin-scheduled-at"
                  type="datetime-local"
                  name="scheduled_at"
                  value={formData.scheduled_at}
                  min={minDatetime}
                  onChange={handleChange}
                  required
                  className="form-input-gaming"
                />
              </div>

              {/* Venue */}
              <div className="form-group-gaming">
                <VenueSearch
                  label="📍 Venue"
                  required
                  value={{
                    venue:          formData.venue,
                    venue_address:  formData.venue_address,
                    venue_lat:      formData.venue_lat,
                    venue_lng:      formData.venue_lng,
                    venue_place_id: formData.venue_place_id,
                    court:          formData.court,
                  }}
                  onChange={handleVenueChange}
                  placeholder="Search park, school, gym..."
                />
              </div>

              {/* Notes */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Additional Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Parking, gear requirements, meetup spot..."
                  rows={2}
                  maxLength={1000}
                  className="form-input-gaming"
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              {/* Recurring toggle */}
              <div className="form-group-gaming">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!formData.is_recurring}
                    onChange={(e) => setFormData((prev) => ({ ...prev, is_recurring: e.target.checked }))}
                    style={{ accentColor: '#38bdf8', width: 16, height: 16 }}
                  />
                  <span className="form-label-gaming" style={{ marginBottom: 0 }}>
                    🔄 Recurring session
                  </span>
                </label>
              </div>

              {formData.is_recurring && (
                <div style={{ background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 8, padding: '0.6rem', marginBottom: '0.3rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <div className="form-group-gaming" style={{ flex: '1 1 130px', marginBottom: 0 }}>
                      <label className="form-label-gaming">Day</label>
                      <select name="recurrence_day" value={formData.recurrence_day} onChange={handleChange} className="dropin-filter-select">
                        <option value="">Select day</option>
                        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => (
                          <option key={i} value={i}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-gaming" style={{ flex: '1 1 100px', marginBottom: 0 }}>
                      <label className="form-label-gaming">Start</label>
                      <input type="time" name="recurrence_time" value={formData.recurrence_time} onChange={handleChange} className="form-input-gaming" />
                    </div>
                    <div className="form-group-gaming" style={{ flex: '1 1 100px', marginBottom: 0 }}>
                      <label className="form-label-gaming">End</label>
                      <input type="time" name="recurrence_end_time" value={formData.recurrence_end_time} onChange={handleChange} className="form-input-gaming" />
                    </div>
                  </div>
                </div>
              )}
            </FloatingCard>

            {/* Col 3: Level & Capacity */}
            <FloatingCard>
              <h3 className="form-section-title">🏅 Level & Capacity</h3>

              {/* Skill level */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Skill Level *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {SKILL_LEVELS.map((sl) => (
                    <label
                      key={sl.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.6rem',
                        padding: '0.4rem 0.65rem',
                        borderRadius: '8px',
                        background: formData.skill_level === sl.value
                          ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.04)',
                        border: formData.skill_level === sl.value
                          ? '1px solid rgba(167,139,250,0.5)' : '1px solid rgba(255,255,255,0.08)',
                        cursor: 'pointer',
                        transition: 'background 0.18s, border-color 0.18s',
                      }}
                    >
                      <input type="radio" name="skill_level" value={sl.value} checked={formData.skill_level === sl.value} onChange={handleChange} style={{ accentColor: '#a78bfa' }} />
                      <div>
                        <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '0.8rem' }}>{sl.label}</div>
                        <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{sl.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Visibility */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Visibility</label>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  {[
                    { value: 'public',  emoji: '🌐', label: 'Public' },
                    { value: 'private', emoji: '🔒', label: 'Private' },
                  ].map((opt) => (
                    <label key={opt.value} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.6rem', borderRadius: '8px',
                      background: formData.visibility === opt.value ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.04)',
                      border: formData.visibility === opt.value ? '1px solid rgba(167,139,250,0.5)' : '1px solid rgba(255,255,255,0.08)',
                      cursor: 'pointer' }}>
                      <input type="radio" name="visibility" value={opt.value} checked={formData.visibility === opt.value} onChange={handleChange} style={{ accentColor: '#a78bfa' }} />
                      <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '0.78rem' }}>{opt.emoji} {opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price per player: shown only while payments are on */}
              <PriceField
                value={formData.price}
                onChange={(price) => setFormData((prev) => ({ ...prev, price }))}
                hint="Leave blank for a free drop-in. Players pay when they take a spot; the waitlist is free."
              />
              <RefundPolicyField
                value={formData.refund_policy}
                price={formData.price}
                onChange={(refund_policy) => setFormData((prev) => ({ ...prev, refund_policy }))}
              />

              {/* Waitlist */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Waitlist — {formData.max_waitlist === '0' || formData.max_waitlist === 0 ? 'off' : `${formData.max_waitlist} max`}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input type="range" name="max_waitlist" min={0} max={20} value={formData.max_waitlist} onChange={handleChange} style={{ flex: 1, accentColor: '#a78bfa' }} />
                  <input type="number" name="max_waitlist" min={0} max={50} value={formData.max_waitlist} onChange={handleChange} className="form-input-gaming" style={{ width: '56px', textAlign: 'center' }} />
                </div>
              </div>

              {/* Max players */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Max Players * — {formData.max_players}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input type="range" name="max_players" min={2} max={50} value={formData.max_players} onChange={handleChange} style={{ flex: 1, accentColor: '#a78bfa' }} />
                  <input type="number" name="max_players" min={2} max={100} value={formData.max_players} onChange={handleChange} className="form-input-gaming" style={{ width: '56px', textAlign: 'center' }} />
                </div>
              </div>

              {/* Game format for team sports (5-a-side, 3x3, …) */}
              {!isIndividualSport && sportFormats.length > 0 && (
              <div className="form-group-gaming">
                <label className="form-label-gaming">Game Format</label>
                <select
                  name="play_format"
                  value={formData.play_format}
                  onChange={handleChange}
                  className="form-select-gaming"
                >
                  {sportFormats.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                  <option value="">Custom team size</option>
                </select>
              </div>
              )}

              {/* Players per team — shown only for team sports; individual sports use play format selector */}
              {!isIndividualSport && (
              <div className="form-group-gaming">
                <label className="form-label-gaming">Per Team — {formData.players_per_team}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input type="range" name="players_per_team" min={1} max={20} value={formData.players_per_team} onChange={handleChange} style={{ flex: 1, accentColor: '#a78bfa' }} />
                  <input type="number" name="players_per_team" min={1} max={50} value={formData.players_per_team} onChange={handleChange} className="form-input-gaming" style={{ width: '56px', textAlign: 'center' }} />
                </div>
              </div>
              )}

              {/* Play Format — only shown for individual sports (badminton, tennis, table tennis) */}
              {isIndividualSport && (
              <div className="form-group-gaming">
                <label className="form-label-gaming">🏸 Play Format</label>
                <select
                  name="play_format"
                  value={formData.play_format}
                  onChange={handleChange}
                  className="form-select-gaming"
                >
                  {(sportFormats.length ? sportFormats : [{ value: 'singles', label: 'Singles (1 vs 1)' }]).map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.4rem' }}>
                  {formData.play_format === 'singles'
                    ? 'Each session match is 1 player vs 1 player.'
                    : 'Each session match is 2 players vs 2 players.'}
                </p>
              </div>
              )}
            </FloatingCard>
          </div>{/* end dropin-form-row */}

          <div className="dropin-form-actions">
            <button type="button" className="dropin-btn dropin-btn--secondary" onClick={() => navigate('/drop-in')} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="dropin-btn dropin-btn--primary" disabled={loading}>
              {loading ? 'Creating…' : '🚀 Create Drop-in'}
            </button>
          </div>
        </form>
      </div>
    </GameLayout>
  );
};

export default DropInCreation;
